import React, { useState, useEffect } from 'react';
import { Smartphone, Edit2, Plus, Clock, Search } from 'lucide-react';
import { supabase, Device } from '../lib/supabase';
import EditDeviceModal from './EditDeviceModal';
import DeviceClaimModal from './DeviceClaimModal';
import DeviceConfigDisplay from './DeviceConfigDisplay';
import { useSupabaseAuth } from '../contexts/SupabaseAuthContext';

interface DevicesPageProps {
  onEditDevice?: (device: Device) => void;
}

const DevicesPageUserFiltered: React.FC<DevicesPageProps> = ({ onEditDevice }) => {
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('all');
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [claimModalOpen, setClaimModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<{ [deviceId: string]: string }>({});

  const { user } = useSupabaseAuth();

  useEffect(() => {
    if (user) {
      loadDevices();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadDevices = async () => {
    if (!user?.id) return;
    
    try {
      setLoading(true);
      
      // Use the RPC function to get only user's devices (respects multi-tenancy)
      const { data, error } = await supabase.rpc('get_user_devices');
      
      if (error) throw error;
      
      console.log('Raw RPC data received:', data);
      
      // Transform the data to match Device interface
      const userDevices = (data || []).map((d: any) => ({
        id: d.device_id,
        device_id: d.device_id,
        device_nickname: d.device_nickname || `Device ${d.device_id}`,
        device_type: d.device_type || 'GPS Tracker',
        is_active: d.is_active ?? false,
        last_seen: d.last_seen,
        last_latitude: d.last_latitude,
        last_longitude: d.last_longitude,
        last_temperature: d.last_temperature,
        last_water_confidence: d.last_water_confidence,
        last_speed: d.last_speed,
        last_heading: d.last_heading,
        battery_percentage: d.battery_percentage,
        cell_signal_strength: d.cell_signal_strength,
        claimed_at: d.claimed_at,
        
        // Device identification fields (NEW)
        firmware_version: d.firmware_version,
        iccid: d.iccid,
        last_firmware_update: d.last_firmware_update,
        // Include all device config fields
        apn: d.apn,
        main_server: d.main_server,
        main_server_port: d.main_server_port,
        tcp_port: d.tcp_port,
        terminal_phone: d.terminal_phone,
        ntp_server: d.ntp_server,
        ntp_ip: d.ntp_ip,
        ntp_port: d.ntp_port,
        report_interval: d.report_interval,
        reporting_interval: d.reporting_interval,
        sample_interval: d.sample_interval,
        stationary_interval: d.stationary_interval,
        movement_distance: d.movement_distance,
        report_interval_movement: d.report_interval_movement,
        movement_speed_threshold: d.movement_speed_threshold,
        temp_humi_alarm_enable: d.temp_humi_alarm_enable,
        temperature_min: d.temperature_min,
        temp_min: d.temp_min,
        temperature_max: d.temperature_max,
        temp_max: d.temp_max,
        humidity_min: d.humidity_min,
        humi_min: d.humi_min,
        humidity_max: d.humidity_max,
        humi_max: d.humi_max,
        temp_humi_time_gap: d.temp_humi_time_gap,
        light_alarm_enable: d.light_alarm_enable,
        light_threshold: d.light_threshold,
        light_alarm_threshold: d.light_alarm_threshold,
        light_alarm_time_gap: d.light_alarm_time_gap,
        g_sensor_alarm_enable: d.g_sensor_alarm_enable,
        g_sensor_threshold: d.g_sensor_threshold,
        g_sensor_time_gap: d.g_sensor_time_gap,
        g_sensor_count: d.g_sensor_count,
        gnss_enable: d.gnss_enable
      }));
      
      console.log('Loaded user devices:', {
        userId: user.id,
        deviceCount: userDevices.length,
        devices: userDevices.map((d: Device) => ({ 
          id: d.device_id, 
          nickname: d.device_nickname,
          main_server: d.main_server,
          tcp_port: d.tcp_port || d.main_server_port,
          apn: d.apn,
          temp_min: d.temperature_min,
          temp_max: d.temperature_max
        }))
      });
      
      // Deduplicate by normalized device_id (last 12 digits), keep the most recent
      const deviceByKey: { [key: string]: Device } = {};
      for (const dev of userDevices) {
        const normalizedKey = (dev.device_id?.replace(/[^0-9]/g, '') || '').slice(-12) || dev.device_id;
        const existing = deviceByKey[normalizedKey];
        if (!existing) {
          deviceByKey[normalizedKey] = dev;
        } else {
          const existingScore = Math.max(
            existing.last_seen ? new Date(existing.last_seen).getTime() : 0,
            existing.claimed_at ? new Date(existing.claimed_at).getTime() : 0
          );
          const currentScore = Math.max(
            dev.last_seen ? new Date(dev.last_seen).getTime() : 0,
            dev.claimed_at ? new Date(dev.claimed_at).getTime() : 0
          );
          if (currentScore >= existingScore) {
            deviceByKey[normalizedKey] = dev;
          }
        }
      }
      const dedupedDevices: Device[] = Object.values(deviceByKey);

      // Initialize all devices with 'all' category selected
      const categoryState: { [key: string]: string } = {};
      dedupedDevices.forEach((device: Device) => {
        categoryState[device.id] = 'all';
      });
      setSelectedCategory(categoryState);

      setDevices(dedupedDevices);
    } catch (error) {
      console.error('Error loading devices:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditDevice = (device: Device) => {
    if (onEditDevice) {
      onEditDevice(device);
    } else {
      setSelectedDevice(device);
      setEditModalOpen(true);
    }
  };

  const handleSaveDevice = async (deviceId: string, newName: string) => {
    try {
      console.log('handleSaveDevice called:', deviceId, newName);
      
      // Simply update the device_nickname in device_settings
      // Since device_id is unique, there's only one record per device
      const { error: updateError } = await supabase
        .from('device_settings')
        .update({ device_nickname: newName })
        .eq('device_id', deviceId);
        
      if (updateError) {
        console.error('Update error:', updateError);
        throw updateError;
      }
      
      console.log('Updated device nickname successfully');
      await loadDevices();
      console.log('Device save completed successfully');
    } catch (error) {
      console.error('Error updating device:', error);
      throw error; // Re-throw so the modal can show the error
    }
  };


  const filteredDevices = devices.filter(device => {
    const matchesSearch = device.device_nickname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         device.device_id.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || 
                         (filterStatus === 'active' && device.is_active) ||
                         (filterStatus === 'inactive' && !device.is_active);
    
    return matchesSearch && matchesStatus;
  });

  const handleCategoryChange = (deviceId: string, category: string) => {
    setSelectedCategory(prev => ({
      ...prev,
      [deviceId]: category
    }));
  };

  const configCategories = [
    { value: 'all', label: 'All Settings' },
    { value: 'network', label: 'Network' },
    { value: 'tracking', label: 'Tracking' },
    { value: 'motion', label: 'Motion' },
    { value: 'environment', label: 'Environment' },
    { value: 'safety', label: 'Safety' },
    { value: 'device', label: 'Device Info' }
  ];

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleString();
  };

  const getDeviceStatus = (device: Device) => {
    if (!device.last_seen) return { status: 'inactive', label: 'Never Connected' };
    
    const lastSeenDate = new Date(device.last_seen);
    const now = new Date();
    const diffHours = (now.getTime() - lastSeenDate.getTime()) / 1000 / 60 / 60;
    
    // Device is active if seen within last 24 hours
    if (diffHours < 24) return { status: 'active', label: 'Online' };
    return { status: 'inactive', label: 'Offline' };
  };

  const getTimeSinceLastSeen = (lastSeen?: string) => {
    if (!lastSeen) return 'Never';
    
    const now = new Date();
    const lastSeenDate = new Date(lastSeen);
    const diffMs = now.getTime() - lastSeenDate.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffDays > 0) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    if (diffHours > 0) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffMins > 0) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    return 'Just now';
  };

  if (loading) {
    return (
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-white rounded-xl p-6">
                <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="max-w-7xl mx-auto px-6 py-6">
      <div className="mb-6">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">My Devices</h2>
        <p className="text-gray-600">Manage and monitor your TraqPack devices</p>
      </div>

      {/* Controls */}
      <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by name or IMEI..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-traqpack-yellow focus:border-transparent"
            />
          </div>
          
          <div className="flex items-center gap-4">
            <div className="flex bg-gray-100 rounded-xl p-1">
              {(['all', 'active', 'inactive'] as const).map(status => (
                <button
                  key={status}
                  onClick={() => setFilterStatus(status)}
                  className={`px-4 py-2 rounded-md font-medium transition-colors ${
                    filterStatus === status
                      ? 'bg-traqpack-yellow text-black'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </button>
              ))}
            </div>
            
            <button
              onClick={() => setClaimModalOpen(true)}
              className="px-4 py-2 bg-traqpack-yellow text-gray-900 rounded-lg hover:bg-traqpack-yellow-dark transition-colors flex items-center gap-2 font-semibold shadow-sm"
            >
              <Plus className="w-5 h-5" />
              Claim Device
            </button>
          </div>
        </div>
      </div>

      {/* Devices Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {filteredDevices.length === 0 ? (
          <div className="col-span-full bg-white rounded-xl shadow-sm p-12 text-center">
            <Smartphone className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {searchTerm ? 'No devices found' : 'No devices yet'}
            </h3>
            <p className="text-gray-600 mb-6">
              {searchTerm 
                ? 'Try adjusting your search criteria' 
                : 'Claim your first TraqPack device to get started'}
            </p>
            {!searchTerm && (
              <button
                onClick={() => setClaimModalOpen(true)}
                className="px-6 py-3 bg-traqpack-yellow text-black rounded-lg hover:bg-yellow-500 transition-colors font-medium"
              >
                Claim Your First Device
              </button>
            )}
          </div>
        ) : (
          filteredDevices.map(device => {
            const deviceStatus = getDeviceStatus(device);
            const currentCategory = selectedCategory[device.id] || 'all';
            return (
              <div key={device.id} className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden border border-gray-200">
                {/* Device Header */}
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-traqpack-yellow/10 rounded-lg flex items-center justify-center">
                        <Smartphone className="w-5 h-5 text-traqpack-yellow-dark" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{device.device_nickname || 'Unnamed Device'}</h3>
                        <p className="text-sm text-gray-500">IMEI: {device.device_id}</p>
                        {device.iccid && (
                          <p className="text-xs text-gray-400 font-mono">ICCID: {device.iccid}</p>
                        )}
                      </div>
                    </div>
                    <div className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                      deviceStatus.status === 'active' ? 'bg-green-100 text-green-700' :
                      deviceStatus.status === 'warning' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      <span className={`inline-block w-2 h-2 rounded-full mr-1 ${
                        deviceStatus.status === 'active' ? 'bg-green-500' :
                        deviceStatus.status === 'warning' ? 'bg-yellow-500' :
                        'bg-gray-500'
                      }`} />
                      {deviceStatus.label}
                    </div>
                  </div>

                  {/* Basic Info */}
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Last Seen</p>
                      <p className="text-sm text-gray-700">
                        {device.last_seen ? formatDate(device.last_seen) : 'Never'}
                      </p>
                    </div>
                    
                    {device.battery_percentage !== undefined && (
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Battery</p>
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                              className={`h-full transition-all ${
                                device.battery_percentage > 50 ? 'bg-green-500' :
                                device.battery_percentage > 20 ? 'bg-yellow-500' :
                                'bg-red-500'
                              }`}
                              style={{ width: `${device.battery_percentage}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium">{device.battery_percentage}%</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Category Selector */}
                  <div className="mb-4">
                    <label className="block text-xs text-gray-500 uppercase tracking-wider mb-1">Configuration</label>
                    <select
                      value={currentCategory}
                      onChange={(e) => handleCategoryChange(device.id, e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-traqpack-yellow focus:border-transparent text-sm"
                    >
                      {configCategories.map(cat => (
                        <option key={cat.value} value={cat.value}>{cat.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Configuration Display */}
                  <DeviceConfigDisplay device={device} category={currentCategory} />

                  {/* Last Update */}
                  <div className="flex items-center space-x-2 text-sm text-gray-600 mt-3">
                    <Clock className="w-4 h-4" />
                    <span>Last seen: {getTimeSinceLastSeen(device.last_seen)}</span>
                  </div>
                </div>

                {/* Device Actions */}
                <div className="p-4 bg-gray-50 border-t border-gray-200">
                  <div className="flex items-center justify-center">
                    <button
                      onClick={() => handleEditDevice(device)}
                      className="flex items-center space-x-2 px-4 py-2 bg-white rounded-lg hover:bg-gray-100 transition-colors duration-200 border border-gray-300"
                    >
                      <Edit2 className="w-4 h-4" />
                      <span className="font-medium">Edit</span>
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Modals */}
      {editModalOpen && selectedDevice && (
        <EditDeviceModal
          isOpen={editModalOpen}
          onClose={() => {
            setEditModalOpen(false);
            setSelectedDevice(null);
          }}
          device={selectedDevice}
          onSave={handleSaveDevice}
        />
      )}

      <DeviceClaimModal
        isOpen={claimModalOpen}
        onClose={() => setClaimModalOpen(false)}
        onDeviceClaimed={loadDevices}
      />
    </main>
  );
};

export default DevicesPageUserFiltered;